import {
  Bell,
  BellOff,
  CheckSquare,
  DollarSign,
  Menu,
  Package,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ManagerBottomNavigationBar from '../../../components/ManagerBottomNavigationBar';
import SidebarMenu from '../../../components/SidebarMenu';
import api from '../../../service/api';
import { pushNotificationService } from '../../../service/push';
import useGroupStore from '../../../store/group';
import { useInventory } from '../../../store/inventory';
import useManagerApprovalsStore from '../../../store/managerApprovals';
import useManagerPurchasesStore from '../../../store/managerPurchases';

interface QuickActionProps {
  category: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  imageSrc?: string;
  onClick?: () => void;
}

const QuickActionCard: React.FC<QuickActionProps> = ({
  category,
  title,
  description,
  icon,
  imageSrc,
  onClick,
}) => (
  <Card
    className="border-0 mb-4 shadow-sm"
    style={{ cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
  >
    <Card.Body className="p-4">
      <Row className="align-items-center">
        <Col xs={8}>
          <Card.Text className="text-muted small mb-1">{category}</Card.Text>
          <Card.Title className="fw-bold mb-2">{title}</Card.Title>
          <Card.Text className="text-muted small">{description}</Card.Text>
        </Col>
        <Col xs={4} className="d-flex justify-content-center">
          {icon ? (
            <div className="bg-primary bg-opacity-10 rounded-3 p-3">{icon}</div>
          ) : imageSrc ? (
            <div
              className="w-100 rounded-3"
              style={{
                aspectRatio: '16/9',
                backgroundImage: `url(${imageSrc})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          ) : null}
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

export default function ManagerHomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { purchases, fetchPurchases } = useManagerPurchasesStore();
  const { approvals, fetchApprovals } = useManagerApprovalsStore();
  const { pendingActivations, refreshPendingActivations } = useInventory();
  const { group, fetchGroup } = useGroupStore();
  const [usersCount, setUsersCount] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    pushNotificationService.getPermissionStatus() === 'granted' &&
      pushNotificationService.isSubscribed(),
  );

  useEffect(() => {
    fetchPurchases();
    fetchApprovals();
    refreshPendingActivations();
    fetchGroup();

    const fetchUsersCount = async () => {
      try {
        const users = await api.usersGetAll();
        setUsersCount(users.length);
      } catch (error) {
        console.error('Failed to fetch users count:', error);
      }
    };

    fetchUsersCount();
  }, [fetchPurchases, fetchApprovals, refreshPendingActivations, fetchGroup]);

  const handleNotificationToggle = async () => {
    if (!notificationsEnabled) {
      const permission = await pushNotificationService.requestPermission();
      setNotificationsEnabled(
        permission === 'granted' && pushNotificationService.isSubscribed(),
      );
    } else {
      await pushNotificationService.unsubscribe();
      setNotificationsEnabled(false);
    }
  };

  const pendingPurchases = purchases.filter(p => p.status === 'Pending');

  const quickActions: QuickActionProps[] = [];

  if (approvals.length > 0) {
    quickActions.push({
      category: t('manager.dashboard.taskApproval'),
      title: t('manager.dashboard.approveTasks', { count: approvals.length }),
      description: t('manager.dashboard.reviewPendingTasks'),
      icon: <CheckSquare size={48} className="text-primary" />,
      onClick: () => navigate('/manager/approvals'),
    });
  }

  if (pendingPurchases.length > 0) {
    quickActions.push({
      category: t('manager.dashboard.purchaseRequests'),
      title: t('manager.dashboard.approveRequests', {
        count: pendingPurchases.length,
      }),
      description: t('manager.dashboard.reviewPurchaseRequests'),
      icon: <DollarSign size={48} className="text-primary" />,
      onClick: () => navigate('/manager/purchases'),
    });
  }

  if (pendingActivations.length > 0) {
    quickActions.push({
      category: t('manager.dashboard.inventoryActivations'),
      title: t('manager.dashboard.processActivations', {
        count: pendingActivations.length,
      }),
      description: t('manager.dashboard.reviewActivationRequests'),
      icon: <Package size={48} className="text-primary" />,
      onClick: () => navigate('/manager/activations'),
    });
  }

  return (
    <div className="bg-white min-vh-100 d-flex flex-column">
      <Container fluid className="flex-grow-1 pb-5">
        {/* Sticky Header */}
        <div
          className="sticky-top bg-white"
          style={{
            zIndex: 1020,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <Row className="align-items-center p-4 pb-2">
            <Col xs="auto">
              <Menu
                size={24}
                className="text-dark"
                onClick={() => setShowSidebar(true)}
                style={{ cursor: 'pointer' }}
              />
            </Col>
            <Col className="text-center">
              <h2 className="text-dark h5 fw-bold mb-0">
                {t('manager.dashboard.title')}
              </h2>
            </Col>
            <Col xs="auto">
              <Button
                variant={notificationsEnabled ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={handleNotificationToggle}
                title={
                  notificationsEnabled
                    ? 'Notifications enabled'
                    : 'Enable notifications'
                }
              >
                {notificationsEnabled ? (
                  <Bell size={20} />
                ) : (
                  <BellOff size={20} />
                )}
              </Button>
            </Col>
          </Row>
        </div>

        {/* Content */}
        <div className="pt-3">
          {/* Statistics Cards */}
          <Row className="g-3 p-4">
            <Col xs={12} md={6}>
              <Card
                className="border-0 bg-light cursor-pointer"
                onClick={() => navigate('/manager/users')}
                style={{ cursor: 'pointer' }}
              >
                <Card.Body className="p-4">
                  <Card.Text className="text-dark mb-2 fw-medium">
                    {t('manager.dashboard.totalUsers')}
                  </Card.Text>
                  <Card.Title className="text-dark h2 fw-bold mb-0">
                    {usersCount}
                  </Card.Title>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card
                className="border-0 bg-light cursor-pointer"
                onClick={() => navigate('/manager/group')}
                style={{ cursor: 'pointer' }}
              >
                <Card.Body className="p-4">
                  <Card.Text className="text-dark mb-2 fw-medium">
                    {t('manager.dashboard.group')}
                  </Card.Text>
                  <Card.Title className="text-dark h2 fw-bold mb-0">
                    {group?.title || 'N/A'}
                  </Card.Title>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Quick Stats Section */}
          {quickActions?.length > 0 && (
            <div className="px-0 pt-3 pb-2">
              <h2 className="text-dark h4 fw-bold">
                {t('manager.dashboard.quickStats')}
              </h2>
            </div>
          )}

          {/* Quick Actions */}
          <div className="px-4">
            {quickActions.map((action, index) => (
              <QuickActionCard key={index} {...action} />
            ))}
          </div>
        </div>
      </Container>

      <SidebarMenu show={showSidebar} onHide={() => setShowSidebar(false)} />
      <ManagerBottomNavigationBar />
    </div>
  );
}
